import {
  adminTributeSchema,
  EMPTY_DOC,
  mediaVariant,
  tributeYears,
  type AdminTribute,
  type RichDoc,
} from '@growup/site-shared';
import { Button, Card, DateInput, Field, Input, Notice, StatusBadge } from '@growup/ui';
import { useCallback, useEffect, useState, type FormEvent } from 'react';
import { z } from 'zod';

import { ApiError, getJson, messageOf, send } from '../api';
import { formatDateTime } from '../format';
import { IMAGE_ASPECTS, ImageField, useImagesBusy } from '../media/ImageUpload';
import { autoSlug } from './PostsView';
import { RichEditor } from './RichEditor';

const BASE = '/api/site/content/memorial';

/**
 * Memorial (decidido pelo GrowuP a 19-10-2026): homenagens a antigos membros
 * que ja nos deixaram. Comeca vazio; so se publica com a autorizacao da
 * familia registada. Ver docs/conteudos.
 */
export function MemorialView() {
  const [list, setList] = useState<AdminTribute[] | null>(null);
  const [editing, setEditing] = useState<string | 'new' | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    getJson(BASE, z.array(adminTributeSchema))
      .then(setList)
      .catch((e: unknown) => setError(messageOf(e)));
  }, []);
  useEffect(load, [load]);

  const move = async (from: number, to: number) => {
    if (!list) return;
    const ids = list.map((t) => t.id);
    const [id] = ids.splice(from, 1);
    if (id === undefined) return;
    ids.splice(to, 0, id);
    setBusy(true);
    setError(null);
    try {
      setList(z.array(adminTributeSchema).parse(await send('PUT', `${BASE}/order`, { ids })));
    } catch (e) {
      setError(messageOf(e));
    } finally {
      setBusy(false);
    }
  };

  if (editing !== null) {
    const current = list?.find((t) => t.id === editing) ?? null;
    return (
      <TributeEditor
        key={editing}
        tribute={current}
        onSaved={(saved) => {
          setList((l) => {
            if (!l) return [saved];
            return l.some((t) => t.id === saved.id)
              ? l.map((t) => (t.id === saved.id ? saved : t))
              : [...l, saved];
          });
          setEditing(saved.id);
        }}
        onClose={() => {
          setEditing(null);
          load();
        }}
      />
    );
  }

  const published = list?.filter((t) => t.status === 'published').length ?? 0;

  return (
    <Card
      headingLevel={2}
      title="Memorial"
      actions={
        <Button size="sm" onClick={() => setEditing('new')}>
          Nova homenagem
        </Button>
      }
    >
      <div className="flex flex-col gap-4">
        <p className="text-gu-ink-muted">
          Homenagens a antigos membros que já nos deixaram, em growup.gg/memorial, por esta ordem.
          Só se publica uma homenagem depois de registar a autorização da família. Enquanto não
          houver nenhuma publicada, o memorial não aparece no rodapé nem em Sobre nós. O texto de
          abertura muda-se em Textos &gt; Memorial.{' '}
          {list ? `${String(published)} no site agora.` : null}
        </p>
        {error ? <Notice tone="danger">{error}</Notice> : null}
        {list === null && !error ? <p>A carregar…</p> : null}
        {list && list.length === 0 ? <p>Ainda não há homenagens.</p> : null}
        <ol className="flex flex-col gap-3">
          {list?.map((t, i) => (
            <li
              key={t.id}
              className="flex flex-wrap items-center gap-4 border-2 border-gu-line p-2"
            >
              {t.photoUrl ? (
                <img
                  src={mediaVariant(t.photoUrl, 'sm')}
                  alt={t.photoAlt}
                  className="aspect-[4/5] w-16 bg-gu-sunken object-cover grayscale"
                />
              ) : (
                <div aria-hidden="true" className="aspect-[4/5] w-16 bg-gu-sunken" />
              )}
              <div className="flex min-w-0 flex-1 flex-col gap-1">
                <span className="font-semibold">
                  {t.nickname}
                  {t.name ? ` · ${t.name}` : ''}
                </span>
                <span className="text-caption text-gu-ink-muted">
                  {tributeYears(t) || 'Sem anos'} · por {t.createdBy}
                </span>
                <span>
                  <StatusBadge tone={t.status === 'published' ? 'success' : 'neutral'}>
                    {t.status === 'published' ? 'No site' : 'Rascunho'}
                  </StatusBadge>
                </span>
              </div>
              <div className="flex gap-1">
                <Button
                  size="sm"
                  variant="ghost"
                  disabled={busy || i === 0}
                  aria-label={`Subir ${t.nickname}`}
                  onClick={() => void move(i, i - 1)}
                >
                  ↑
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  disabled={busy || i === list.length - 1}
                  aria-label={`Descer ${t.nickname}`}
                  onClick={() => void move(i, i + 1)}
                >
                  ↓
                </Button>
                <Button size="sm" variant="secondary" onClick={() => setEditing(t.id)}>
                  Editar
                </Button>
              </div>
            </li>
          ))}
        </ol>
      </div>
    </Card>
  );
}

const yearValue = (v: string): number | null => (v.trim() === '' ? null : Number(v));

function TributeEditor({
  tribute,
  onSaved,
  onClose,
}: {
  tribute: AdminTribute | null;
  onSaved: (t: AdminTribute) => void;
  onClose: () => void;
}) {
  const [nickname, setNickname] = useState(tribute?.nickname ?? '');
  const [slug, setSlug] = useState(tribute?.slug ?? '');
  const [name, setName] = useState(tribute?.name ?? '');
  const [photoUrl, setPhotoUrl] = useState<string | null>(tribute?.photoUrl ?? null);
  const [photoAlt, setPhotoAlt] = useState(tribute?.photoAlt ?? '');
  const [yearFrom, setYearFrom] = useState(tribute?.yearFrom?.toString() ?? '');
  const [yearTo, setYearTo] = useState(tribute?.yearTo?.toString() ?? '');
  const [body, setBody] = useState<RichDoc>(tribute?.body ?? EMPTY_DOC);
  const [consentBy, setConsentBy] = useState(tribute?.consentBy ?? '');
  const [consentOn, setConsentOn] = useState(tribute?.consentOn ?? '');
  const [fields, setFields] = useState<Record<string, string>>({});
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const photos = useImagesBusy();

  const isPublished = tribute?.status === 'published';
  // A autorizacao guardada (nao a que esta a ser escrita) e a que conta para publicar.
  const consentSaved = tribute !== null && tribute.consentBy !== '' && tribute.consentOn !== null;

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    const payload = {
      slug,
      nickname,
      name,
      photoUrl,
      photoAlt: photoUrl ? photoAlt : '',
      yearFrom: yearValue(yearFrom),
      yearTo: yearValue(yearTo),
      body,
      consentBy,
      consentOn: consentOn === '' ? null : consentOn,
    };
    setBusy(true);
    setError(null);
    setSaved(null);
    setFields({});
    try {
      const result = tribute
        ? await send('PUT', `${BASE}/${tribute.id}`, payload)
        : await send('POST', BASE, payload);
      onSaved(adminTributeSchema.parse(result));
      setSaved(tribute ? 'Guardado.' : 'Criada como rascunho.');
    } catch (err) {
      setError(messageOf(err));
      if (err instanceof ApiError) setFields(err.fields);
    } finally {
      setBusy(false);
    }
  };

  const run = async (path: string) => {
    if (!tribute) return;
    setBusy(true);
    setError(null);
    setSaved(null);
    try {
      onSaved(adminTributeSchema.parse(await send('POST', `${BASE}/${tribute.id}${path}`)));
    } catch (e) {
      setError(messageOf(e));
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    if (!tribute) return;
    if (!window.confirm(`Apagar a homenagem a ${tribute.nickname}? Não se pode desfazer.`)) return;
    setBusy(true);
    try {
      await send('DELETE', `${BASE}/${tribute.id}`);
      onClose();
    } catch (e) {
      setError(messageOf(e));
      setBusy(false);
    }
  };

  return (
    <div className="flex flex-col gap-4">
      <div>
        <Button variant="ghost" size="sm" onClick={onClose}>
          ← Memorial
        </Button>
      </div>
      <Card headingLevel={2} title={tribute ? `Homenagem a ${tribute.nickname}` : 'Nova homenagem'}>
        <form className="flex flex-col gap-4" onSubmit={(e) => void submit(e)} noValidate>
          <div className="grid items-start gap-4 md:grid-cols-[14rem_1fr]">
            <div className="flex flex-col gap-3">
              <ImageField
                label="Foto"
                optional
                value={photoUrl}
                onChange={setPhotoUrl}
                aspect={IMAGE_ASPECTS.memorial}
                onBusyChange={photos.track('foto')}
                error={fields.photoUrl}
                hint="4:5, como as cartas de membro. No site aparece a preto e branco e ganha cor ao passar o rato."
              />
              {photoUrl ? (
                <Field label="Descrição da foto" hint="Para quem não a vê." error={fields.photoAlt}>
                  <Input
                    value={photoAlt}
                    maxLength={300}
                    onChange={(e) => setPhotoAlt(e.target.value)}
                  />
                </Field>
              ) : null}
            </div>
            <div className="grid gap-3 sm:grid-cols-2">
              <Field label="Alcunha" error={fields.nickname}>
                <Input
                  value={nickname}
                  maxLength={60}
                  onChange={(e) => {
                    setNickname(e.target.value);
                    if (!tribute) setSlug(autoSlug(e.target.value));
                  }}
                />
              </Field>
              <Field
                label="Endereço"
                hint={`growup.gg/memorial/${slug || '…'}`}
                error={fields.slug}
              >
                <Input value={slug} onChange={(e) => setSlug(e.target.value)} />
              </Field>
              <Field
                label="Nome"
                optional
                hint="Só com autorização da família. Vazio = só a alcunha."
                error={fields.name}
              >
                <Input value={name} maxLength={120} onChange={(e) => setName(e.target.value)} />
              </Field>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Membro desde" optional error={fields.yearFrom}>
                  <Input
                    inputMode="numeric"
                    placeholder="aaaa"
                    maxLength={4}
                    value={yearFrom}
                    onChange={(e) => setYearFrom(e.target.value.replace(/\D/g, ''))}
                  />
                </Field>
                <Field label="Até" optional error={fields.yearTo}>
                  <Input
                    inputMode="numeric"
                    placeholder="aaaa"
                    maxLength={4}
                    value={yearTo}
                    onChange={(e) => setYearTo(e.target.value.replace(/\D/g, ''))}
                  />
                </Field>
              </div>
            </div>
          </div>
          <RichEditor
            label="Texto da homenagem"
            value={body}
            onChange={setBody}
            error={fields.body ?? fields['']}
            hint="Pode ter fotos e vídeos, como as notícias."
          />
          <fieldset className="flex flex-col gap-3 border-2 border-gu-warning/60 p-3">
            <legend className="px-1 font-sub text-label font-bold uppercase">
              Autorização da família
            </legend>
            <p className="text-caption text-gu-ink-muted">
              Obrigatória para publicar. Fica só no back-office: o site nunca a mostra.
            </p>
            <div className="grid gap-3 sm:grid-cols-[1fr_12rem]">
              <Field
                label="Quem autorizou"
                hint="Ex.: «A mãe, por email»."
                error={fields.consentBy}
              >
                <Input
                  value={consentBy}
                  maxLength={200}
                  onChange={(e) => setConsentBy(e.target.value)}
                />
              </Field>
              <Field label="Quando" error={fields.consentOn}>
                <DateInput value={consentOn} onChange={setConsentOn} />
              </Field>
            </div>
          </fieldset>
          {error ? <Notice tone="danger">{error}</Notice> : null}
          {saved ? <Notice tone="success">{saved}</Notice> : null}
          <div className="flex flex-wrap items-center gap-2">
            <Button type="submit" disabled={busy || photos.busy}>
              {tribute ? 'Guardar' : 'Criar rascunho'}
            </Button>
            {tribute && !isPublished ? (
              <Button
                type="button"
                variant="secondary"
                disabled={busy || !consentSaved}
                onClick={() => void run('/publish')}
              >
                Publicar
              </Button>
            ) : null}
            {isPublished ? (
              <Button
                type="button"
                variant="secondary"
                disabled={busy}
                onClick={() => void run('/unpublish')}
              >
                Tirar do site
              </Button>
            ) : null}
            {tribute && !isPublished ? (
              <Button type="button" variant="ghost" disabled={busy} onClick={() => void remove()}>
                Apagar
              </Button>
            ) : null}
          </div>
          {tribute ? (
            <p className="text-caption text-gu-ink-muted">
              {isPublished ? (
                <>
                  No site.{' '}
                  <a
                    className="gu-link"
                    href={`/memorial/${tribute.slug}`}
                    target="_blank"
                    rel="noreferrer"
                  >
                    Ver no site
                  </a>
                  .{' '}
                </>
              ) : consentSaved ? (
                'Rascunho: ainda não aparece no site. '
              ) : (
                'Rascunho. Para publicar, registe e guarde a autorização da família. '
              )}
              Última alteração: {formatDateTime(tribute.updatedAt)} por {tribute.updatedBy}.
            </p>
          ) : null}
        </form>
      </Card>
    </div>
  );
}
